import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { SeoHead } from '../components/SeoHead'

export function HomePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [keyword, setKeyword] = useState('')
  const [location, setLocation] = useState('')
  const [category, setCategory] = useState('')

  const { data: configData } = useQuery({
    queryKey: ['config'],
    queryFn: () => api.config(),
  })

  const { data: recentJobs } = useQuery({
    queryKey: ['recent-jobs'],
    queryFn: () => api.searchJobs({ sort: 'recent', per_page: 6 }),
  })

  const { data: companiesData } = useQuery({
    queryKey: ['top-companies'],
    queryFn: () => api.companies({ per_page: 6, verified_only: true }),
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const query = new URLSearchParams()
    if (keyword) query.set('q', keyword)
    if (location) query.set('location_id', location)
    if (category) query.set('category_id', category)
    navigate(`/jobs?${query.toString()}`)
  }

  const categories = (configData?.data?.categories as Array<{ id: number; name: string; slug: string; icon?: string }>) || [
    { id: 1, name: 'Software Engineering', slug: 'software-engineering', icon: '💻' },
    { id: 2, name: 'Product & Design', slug: 'product-design', icon: '🎨' },
    { id: 3, name: 'Data & AI / ML', slug: 'data-ai', icon: '🤖' },
    { id: 4, name: 'Sales & Marketing', slug: 'sales-marketing', icon: '📈' },
    { id: 5, name: 'Finance & Accounting', slug: 'finance', icon: '💰' },
    { id: 6, name: 'Customer Support', slug: 'support', icon: '🎧' },
  ]

  const locations = (configData?.data?.locations as Array<{ id: number; name: string }>) || [
    { id: 1, name: 'Bengaluru' },
    { id: 2, name: 'Mumbai' },
    { id: 3, name: 'Delhi NCR' },
    { id: 4, name: 'Kolkata & WB' },
    { id: 5, name: 'Hyderabad' },
    { id: 6, name: 'Pune' },
    { id: 8, name: 'Remote' },
  ]

  return (
    <div className="home-page">
      <SeoHead
        title="Verified Jobs, Smart Matching & Better Hiring"
        description="Explore 100% verified jobs with transparent take-home salary breakdowns, fresher launchpad, and direct hiring from top companies across India."
        canonicalPath="/"
      />
      {/* Hero Section */}
      <section className="hero-banner" style={{ textAlign: 'center', padding: '3.5rem 1rem 3rem' }}>
        <div className="hero-content" style={{ maxWidth: '880px', margin: '0 auto' }}>
          <div className="hero-badge" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            background: 'rgba(99, 102, 241, 0.1)',
            color: '#4f46e5',
            padding: '0.35rem 0.9rem',
            borderRadius: '9999px',
            fontSize: '0.85rem',
            fontWeight: 700,
            marginBottom: '1rem'
          }}>
            🇮🇳 India’s Smart & Transparent Hiring Platform
          </div>
          <h1 className="hero-headline" style={{ fontSize: '2.8rem', fontWeight: 800, lineHeight: 1.2, margin: '0 0 1rem' }}>
            Verified Jobs. Smart Matching. <span className="gradient-text">Better Hiring.</span>
          </h1>
          <p className="hero-desc" style={{ fontSize: '1.15rem', color: 'var(--ink-secondary, #334155)', lineHeight: 1.6, maxWidth: '720px', margin: '0 auto 2rem' }}>
            Escape ghost jobs and scam recruiters. Explore 100% verified positions across Kolkata, Bangalore, Mumbai, and remote teams with upfront take-home salary transparency.
          </p>

          {/* Strategic Search Bar */}
          <form className="hero-search-box" onSubmit={handleSearch} role="search" style={{ margin: '0 auto 2.5rem' }}>
            <div className="search-field">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Job title, skill (e.g. React, GST, Python), or company..."
                aria-label="Job keywords"
              />
            </div>

            <div className="search-field border-left">
              <span className="search-icon">📍</span>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                aria-label="Filter by location"
              >
                <option value="">All Regions (India / Remote)</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="search-field border-left">
              <span className="search-icon">📁</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">All Domains</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <button type="submit" className="search-submit-btn">
              Find Jobs
            </button>
          </form>

          {/* Strategic Pillars Feature Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', textAlign: 'left' }}>
            <Link to="/safety" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="card" style={{ padding: '1rem 1.25rem', border: '1px solid var(--rule, #e2e8f0)', borderRadius: '10px', height: '100%' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>🛡️</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.2rem' }}>4-Tier Verification</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>GSTIN & domain checked recruiters. Zero ghost jobs.</div>
              </div>
            </Link>
            <Link to="/freshers" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="card" style={{ padding: '1rem 1.25rem', border: '1px solid var(--rule, #e2e8f0)', borderRadius: '10px', height: '100%' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>🎓</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.2rem' }}>Fresher Launchpad</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>Curated 0-experience roles & paid internships.</div>
              </div>
            </Link>
            <Link to="/assessments" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="card" style={{ padding: '1rem 1.25rem', border: '1px solid var(--rule, #e2e8f0)', borderRadius: '10px', height: '100%' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>⚡</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.2rem' }}>Skill Badges</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>Free tests in Excel, GST & coding for instant hiring trust.</div>
              </div>
            </Link>
            <Link to="/referrals" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="card" style={{ padding: '1rem 1.25rem', border: '1px solid var(--rule, #e2e8f0)', borderRadius: '10px', height: '100%' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>🎁</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.2rem' }}>Refer & Earn ₹500</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>Invite peers & get rewarded on verified placements.</div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Fresher Spotlight Banner */}
      <section className="section-container" style={{ padding: '1.5rem 1rem' }}>
        <div style={{
          background: 'linear-gradient(135deg, #312e81 0%, #4338ca 100%)',
          borderRadius: '12px',
          padding: '1.75rem 2rem',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1.5rem'
        }}>
          <div>
            <span style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', background: 'rgba(255,255,255,0.2)', padding: '0.2rem 0.6rem', borderRadius: '9999px', fontWeight: 700 }}>
              Special Focus
            </span>
            <h2 style={{ fontSize: '1.5rem', margin: '0.5rem 0 0.35rem', fontWeight: 800 }}>
              Are you a College Graduate or Entry-Level Seeker?
            </h2>
            <p style={{ margin: 0, color: '#e0e7ff', fontSize: '0.95rem' }}>
              Explore over 1,200+ verified entry-level roles, management traineeships, and stipended internships.
            </p>
          </div>
          <Link to="/freshers" className="btn" style={{ background: '#fff', color: '#312e81', fontWeight: 700, padding: '0.75rem 1.5rem' }}>
            Visit Fresher Hub →
          </Link>
        </div>
      </section>

      {/* Popular Categories Section */}
      <section className="section-container">
        <div className="section-header">
          <div>
            <h2 className="section-title">Explore by Domain</h2>
            <p className="section-subtitle">Discover verified openings across high-demand Indian industries</p>
          </div>
          <Link to="/jobs" className="view-all-link">
            Browse all jobs →
          </Link>
        </div>

        <div className="category-cards-grid">
          {categories.slice(0, 6).map((cat) => (
            <Link key={cat.id} to={`/jobs?category_id=${cat.id}`} className="category-card">
              <div className="cat-icon">{cat.icon || '💼'}</div>
              <div className="cat-info">
                <h3>{cat.name}</h3>
                <span className="cat-count">Explore Openings ↗</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Top Hiring Companies */}
      <section className="section-container bg-surface-alt">
        <div className="section-header">
          <div>
            <h2 className="section-title">Top Verified Employers</h2>
            <p className="section-subtitle">GSTIN-authenticated organizations actively hiring</p>
          </div>
          <Link to="/companies" className="view-all-link">
            View all companies →
          </Link>
        </div>

        <div className="companies-cards-grid">
          {companiesData?.data && companiesData.data.length > 0 ? (
            companiesData.data.slice(0, 4).map((comp) => (
              <Link key={comp.slug} to={`/companies/${comp.slug}`} className="company-showcase-card">
                <div className="comp-avatar">
                  {comp.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="comp-meta">
                  <div className="comp-name-wrap">
                    <h3 className="comp-name">{comp.name}</h3>
                    {comp.is_verified && <span className="verified-badge" title="GSTIN Verified Employer">🛡️ Verified</span>}
                  </div>
                  <p className="comp-industry">{comp.industry?.name || 'Corporate Services'}</p>
                  <span className="comp-jobs-badge">{comp.open_jobs || 1} open roles</span>
                </div>
              </Link>
            ))
          ) : (
            <div className="loading-placeholder">Loading verified employers…</div>
          )}
        </div>
      </section>

      {/* Featured / Recent Jobs */}
      <section className="section-container">
        <div className="section-header">
          <div>
            <h2 className="section-title">Latest Verified Openings</h2>
            <p className="section-subtitle">Recently published roles with clear in-hand salary disclosures</p>
          </div>
          <Link to="/jobs" className="view-all-link">
            Explore all {recentJobs?.meta.total || 'open'} positions →
          </Link>
        </div>

        <div className="recent-jobs-grid">
          {recentJobs?.data?.map((job) => (
            <Link key={job.id} to={`/jobs/${job.slug}`} className="featured-job-card" style={{ textDecoration: 'none' }}>
              <div className="job-card-header">
                <div>
                  <h3 className="job-title">{job.title}</h3>
                  <div className="job-company">{job.company?.name || 'Verified Company'}</div>
                </div>
                <span className="job-mode-pill">{job.work_mode.toUpperCase()}</span>
              </div>

              <div className="job-tags">
                <span className="tag-chip">📍 {job.location?.name || 'Remote'}</span>
                <span className="tag-chip">💼 {job.employment_type.replace('_', ' ')}</span>
                {job.salary && (
                  <span className="tag-chip tag-salary">
                    💰 ₹{(job.salary.min ? job.salary.min / 100000 : 3)} - {(job.salary.max ? job.salary.max / 100000 : 15)} LPA
                  </span>
                )}
                {job.is_fresher_friendly && (
                  <span className="tag-chip" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#4f46e5', fontWeight: 600 }}>
                    🎓 Fresher
                  </span>
                )}
              </div>

              <div className="job-skills-row">
                {job.skills?.slice(0, 3).map((s) => (
                  <span key={s.id} className="skill-pill">{s.name}</span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Dual Value Proposition Section (Shown only when not signed in) */}
      {!user && (
        <section className="dual-cta-section">
          <div className="cta-card candidate-cta">
            <div className="cta-icon">🎯</div>
            <h2>For Job Seekers & Freshers</h2>
            <p>
              Build ATS-friendly CVs, take free skill assessments, get direct access to GSTIN-verified recruiters, and track applications in real time.
            </p>
            <div className="cta-actions">
              <Link to="/register?role=candidate" className="btn btn-primary">Create Free Candidate Profile</Link>
              <Link to="/freshers" className="btn btn-secondary">Explore Fresher Hub</Link>
            </div>
          </div>

          <div className="cta-card employer-cta">
            <div className="cta-icon">🏢</div>
            <h2>For Verified Hiring Teams</h2>
            <p>
              Post vacancies with salary transparency, authenticate with GSTIN, filter candidates with custom knockouts, and manage 8-stage ATS pipelines.
            </p>
            <div className="cta-actions">
              <Link to="/register?role=employer" className="btn btn-emerald">Start Hiring Verified Talent</Link>
              <Link to="/employer" className="btn btn-outline-white">Recruiter Console</Link>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
